import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  COOKIE_CONSENT_STORAGE_KEY,
  OPEN_COOKIE_SETTINGS_EVENT,
  type AnalyticsConsent,
} from '../../config/compliance';
import { disableAnalytics, enableAnalytics } from '../../lib/analytics';

function readConsentValue(): AnalyticsConsent {
  if (typeof window === 'undefined') return 'unset';

  const rawValue = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
  if (rawValue === 'granted' || rawValue === 'denied') {
    return rawValue;
  }
  return 'unset';
}

function persistConsentValue(value: Exclude<AnalyticsConsent, 'unset'>): void {
  window.localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, value);
}

export function openCookieSettings(): void {
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
}

export default function CookieConsentBanner() {
  const location = useLocation();
  const [consent, setConsent] = useState<AnalyticsConsent>(() => readConsentValue());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(consent === 'granted');
  const isImmersiveRoute = /^\/(?:result|spread-layout|stitch\/selection|payment\/checkout|lab)(?:\/|$)/.test(location.pathname);
  const useCompactLayout = isImmersiveRoute && !settingsOpen;

  useEffect(() => {
    setAnalyticsEnabled(consent === 'granted');
  }, [consent]);

  useEffect(() => {
    if (consent === 'granted') {
      enableAnalytics();
      return;
    }
    if (consent === 'denied') {
      disableAnalytics();
    }
  }, [consent]);

  useEffect(() => {
    const handleOpenSettings = () => {
      setSettingsOpen(true);
    };

    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);
    return () => {
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenSettings);
    };
  }, []);

  const showBanner = consent === 'unset' || settingsOpen;

  const applyDecision = (value: Exclude<AnalyticsConsent, 'unset'>) => {
    persistConsentValue(value);
    setConsent(value);
    setSettingsOpen(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <aside
      className={`fixed inset-x-3 bottom-3 z-[120] max-h-[min(32vh,14rem)] overflow-y-auto rounded-2xl border border-[#c5a065]/30 bg-[#14110f]/94 px-3 py-3 shadow-[0_20px_60px_-28px_rgba(0,0,0,0.92)] backdrop-blur-md ${
        isImmersiveRoute
          ? 'md:inset-x-auto md:bottom-4 md:right-4 md:w-[min(360px,calc(100vw-2rem))]'
          : 'md:inset-x-auto md:bottom-6 md:right-6 md:w-[min(296px,calc(100vw-3rem))] md:px-4 md:py-4'
      }`}
    >
      <div className="flex w-full flex-col gap-2.5">
        <div>
          <p className="text-[12px] font-medium leading-5 text-[#f5e2c7] md:text-[13px] md:leading-6">
            분석 쿠키로 사용 흐름과 오류를 개선합니다.
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-[#e3d5ca]/70 md:hidden">
            {settingsOpen ? '분석 수집 on/off를 여기서 바로 바꿀 수 있습니다.' : '동의 여부는 언제든 다시 바꿀 수 있습니다.'}
          </p>
          <p className="mt-1 hidden text-[11px] leading-relaxed text-[#e3d5ca]/70 md:block">
            {settingsOpen
              ? '분석 데이터는 서비스 통계, 오류 확인, UX 개선 용도로만 사용합니다.'
              : '통계와 오류 확인에만 사용합니다. 동의 여부는 언제든 다시 바꿀 수 있습니다.'}
          </p>
        </div>

        {settingsOpen && (
          <div className="border border-[#c5a065]/20 bg-black/20 px-3 py-3 text-xs text-[#e3d5ca]/75">
            <div className="flex items-center justify-between py-1">
              <span>필수 쿠키(항상 활성화)</span>
              <span className="text-[#c5a065]">켜짐</span>
            </div>
            <div className="mt-2 flex items-center justify-between py-1">
              <span>분석 쿠키(GA4 / Clarity)</span>
              <button
                type="button"
                onClick={() => setAnalyticsEnabled((prev) => !prev)}
                className={`inline-flex min-w-[58px] items-center justify-center border px-2 py-1 text-[11px] tracking-[0.08em] transition-colors ${
                  analyticsEnabled
                    ? 'border-[#c5a065] bg-[#2a211b] text-[#f5e2c7]'
                    : 'border-[#5a4a3d] bg-[#1b1714] text-[#b9a89a]'
                }`}
              >
                {analyticsEnabled ? '활성화' : '비활성화'}
              </button>
            </div>
          </div>
        )}

        <div className={`gap-2 text-[11px] sm:text-xs ${useCompactLayout ? 'grid grid-cols-3' : 'grid grid-cols-3 sm:flex sm:flex-wrap sm:items-center'}`}>
          <button
            type="button"
            onClick={() => applyDecision('granted')}
            className="border border-[#c5a065] bg-[#2a211b] px-3 py-2 tracking-[0.12em] text-[#f5e2c7] transition-colors hover:bg-[#3a2d22]"
          >
            동의
          </button>
          <button
            type="button"
            onClick={() => applyDecision('denied')}
            className="border border-[#5a4a3d] bg-[#1b1714] px-3 py-2 tracking-[0.12em] text-[#d9c9bb] transition-colors hover:border-[#8a6f58]"
          >
            거부
          </button>
          <button
            type="button"
            onClick={() => setSettingsOpen((prev) => !prev)}
            className="border border-[#5a4a3d] bg-transparent px-3 py-2 tracking-[0.12em] text-[#d9c9bb] transition-colors hover:border-[#8a6f58]"
          >
            설정
          </button>
          {!useCompactLayout && settingsOpen && (
            <Link
              to="/privacy"
              className="border border-[#5a4a3d] px-3 py-2 text-center tracking-[0.12em] text-[#d9c9bb] transition-colors hover:border-[#8a6f58]"
            >
              자세히
            </Link>
          )}

          {settingsOpen && (
            <button
              type="button"
              onClick={() => applyDecision(analyticsEnabled ? 'granted' : 'denied')}
              className="col-span-2 border border-[#c5a065]/80 bg-[#2a211b] px-3 py-2 tracking-[0.14em] text-[#f5e2c7] transition-colors hover:bg-[#3a2d22] sm:ml-auto sm:col-span-1"
            >
              설정 저장
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}

